import { useEffect, useState } from "react";
import { Hairline } from "@/components/avant/Avant";
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from "framer-motion";
import { api, assetUrl, squareSet } from "@/lib/api";
import { Price, ServePrices } from "@/components/z6/Price";
import { useFilters } from "@/context/FiltersContext";
import { ScienceReveal } from "@/components/z6/Science";
import { dishTheme } from "@/lib/menuTheme";
import { AllergenIcon } from "@/components/z6/AllergenIcon";
import { Badge, PatternPanel, imprintKey } from "@/components/z6/Pattern";
import { AllergenKey } from "@/components/z6/AllergenKey";
import { useTrack } from "@/lib/track";
import { VegMark } from "@/components/z6/VegMark";

const CountUp = ({ to }) => {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  useEffect(() => {
    const c = animate(mv, to, { duration: 1.6, delay: 0.55, ease: [0.16, 1, 0.3, 1] });
    return c.stop;
  }, [to, mv]);
  return <motion.span>{rounded}</motion.span>;
};

// Pattern square inside a large circle; rotating ring text carries the dish name and its menu.
const Visual = ({ dish, menuNames }) => (
  <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} className="relative mx-auto aspect-square w-full max-w-[440px]">
    <svg aria-hidden viewBox="0 0 1000 1000" className="absolute inset-0 h-full w-full overflow-visible">
      <motion.circle cx="500" cy="500" r="480" fill="none" stroke="#0B2E44" strokeOpacity="0.3" strokeWidth="1.5" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.8, ease: [0.65, 0, 0.35, 1] }} />
      <circle cx="500" cy="500" r="372" fill="none" stroke="#E8508A" strokeOpacity="0.8" strokeWidth="1.5" />
      <defs>
        <path id={`dr-top-${dish.id}`} d="M 82 500 A 418 418 0 0 1 918 500" />
        <path id={`dr-bot-${dish.id}`} d="M 60 500 A 440 440 0 0 0 940 500" />
      </defs>
      <text fill="#0B2E44" fontSize="34" fontFamily="Switzer, sans-serif" fontWeight="400" letterSpacing="0.16em"><textPath href={`#dr-top-${dish.id}`} startOffset="50%" textAnchor="middle">{dish.name.toUpperCase()}</textPath></text>
      <text fill="#B8326C" fontSize="34" fontFamily="Switzer, sans-serif" fontWeight="400" letterSpacing="0.22em"><textPath href={`#dr-bot-${dish.id}`} startOffset="50%" textAnchor="middle">{(menuNames?.[dish.menus?.[0]] || dish.section || "Zone 6").toUpperCase()}</textPath></text>
    </svg>
    <div className={`absolute overflow-hidden ${dish.square_photo ? "inset-[24%] rounded-[14px_14px_14px_3px]" : "inset-[17%] rounded-full"}`}>
      {dish.square_photo
        ? <img src={assetUrl(dish.square_photo)} srcSet={squareSet(dish.square_photo)} sizes="(min-width: 1024px) 40vw, 70vw" width={1200} height={1200} decoding="async" alt={dish.name} data-testid="dish-detail-photo" className="h-full w-full object-cover" />
        : <PatternPanel course={dish.course} imprint={imprintKey(dish)} seed={dish.id} testId="dish-detail-gradient" className="h-full w-full" />}
    </div>
    <Badge dish={dish} testId="dish-detail-badge" className="absolute bottom-[14%] left-[16%] h-14 w-14" />
  </motion.div>
);

const PairWith = ({ dish, onOpenDish }) => {
  const { filters } = useFilters();
  const [drink, setDrink] = useState(null);
  useEffect(() => {
    setDrink(null);
    if (dish.course === "beverage") return;
    api.get(`/dishes/${dish.id}/pairing`, { params: { avoid: filters.avoid.join(",") || undefined, diet: filters.veg } }).then((r) => setDrink(r.data.drink)).catch(() => {});
  }, [dish.id, dish.course, filters.avoid, filters.veg]);
  if (!drink) return null;
  return (
    <button type="button" data-testid="dish-pair-with" onClick={() => onOpenDish?.(drink)}
      className="mt-5 flex min-h-[56px] w-full max-w-md items-center gap-3 rounded-2xl bg-white/80 p-2.5 pr-4 text-left ring-1 ring-[#0B2E44]/10 active:ring-[#F08DB5] lg:mx-0">
      <PatternPanel course="beverage" imprint="beverage" className="relative h-11 w-11 shrink-0 rounded-xl" />
      <span className="min-w-0 flex-1"><span className="font-mono block text-[15px] uppercase tracking-[0.22em] text-[#B8326C]">Pair it with</span>
        <span data-testid="dish-pair-with-name" className="font-ui block text-[15px] text-[#0B2E44]">{drink.name}</span></span>
      <Price value={drink.serves?.[0]?.price_inr ?? drink.price_inr} size="row" />
    </button>
  );
};

const norm = (x) => x.toLowerCase().replace(/[^a-z ]/g, "").trim();
// Drop a description that only restates the ingredient list.
const describes = (dish) => {
  if (!dish.description) return false;
  const parts = dish.description.split(/,|&|\band\b|\bwith\b|·|\//i).map(norm).filter(Boolean);
  const ings = dish.ingredients.map(norm);
  const listed = parts.filter((p) => ings.some((i) => i && (i.includes(p) || p.includes(i)))).length;
  return parts.length === 0 ? false : listed / parts.length < 0.6;
};

const Label = ({ children }) => <p className="mb-2 font-mono text-[15px] uppercase tracking-[0.25em] text-[#B8326C]">{children}</p>;

export const DishDetail = ({ dish, onClose, onOpenDish, menuNames }) => {
  useTrack("dish_opened", { dish_id: dish?.id }, !!dish);
  useEffect(() => {
    if (!dish) return undefined;
    document.body.dataset.modal = "1";
    return () => { delete document.body.dataset.modal; };
  }, [dish]);
  useEffect(() => {
    if (!dish) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dish, onClose]);
  const t = dish ? dishTheme(dish) : null;
  return (
    <AnimatePresence>
      {dish && (
        <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{ opacity: 1 }} exit={{ opacity: 1 }}>
          <motion.div data-testid="dish-detail-backdrop" className="absolute inset-0 bg-[#BFE8EC]/55 backdrop-blur-md" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
          <motion.div layoutId={`dish-${dish.id}`} role="dialog" aria-modal="true" data-testid="dish-detail-sheet"
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="detail-glass relative max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-t-[32px] sm:mx-6 sm:rounded-[32px]">
            <button data-testid="dish-detail-close-btn" onClick={onClose} aria-label="Close"
              className="absolute right-4 top-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-[#0B2E44]/15 bg-white/85 text-[#0B2E44] backdrop-blur-md">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
            <div className="px-6 pb-10 pt-10 sm:px-10 lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-start lg:gap-10 landscape:md:grid landscape:md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] landscape:md:items-start landscape:md:gap-8">
              <div data-testid="dish-detail-visual" className="relative lg:sticky lg:top-4 landscape:md:sticky landscape:md:top-4"><Visual dish={dish} menuNames={menuNames} /></div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }} className="relative mx-auto mt-12 max-w-3xl text-center lg:mt-2 lg:text-left landscape:md:mt-2 landscape:md:text-left">
                <h2 data-testid="dish-detail-name" className="font-title text-[40px] uppercase leading-[1.05] tracking-[0.03em] text-[#0B2E44]">{dish.name}</h2>
                <Hairline className="mt-5" color="#E8508A" opacity={0.7} />
                {describes(dish) && <p data-testid="dish-detail-description" className="font-ui mx-auto mt-4 max-w-2xl text-[17px] leading-relaxed text-[#0B2E44] lg:mx-0">{dish.description}</p>}
                {dish.suitability_note && <p data-testid="dish-detail-hold" className="font-ui mx-auto mt-3 max-w-2xl text-[16px] text-[#B8326C] lg:mx-0">{dish.suitability_note}</p>}
                <PairWith dish={dish} onOpenDish={onOpenDish} />
                <div data-testid="dish-detail-info" className="font-ui mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[15px] text-[#0B2E44] lg:justify-start landscape:md:justify-start">
                  {dish.kcal != null && <span data-testid="dish-detail-kcal" className="font-mono text-[15px] text-[#0B2E44]">~<CountUp to={dish.kcal} /> kcal</span>}
                  <span aria-hidden className="h-1 w-1 rounded-full bg-[#F08DB5]" />
                  <span data-testid="dish-detail-allergens" className="inline-flex flex-wrap items-center gap-2">
                    {dish.allergens.length ? dish.allergens.map((a) => <span key={a} className="inline-flex items-center gap-1 text-[#B00020]"><AllergenIcon name={a} className="h-3.5 w-3.5" />{a}</span>)
                      : <span className="text-[#0B2E44]">No listed allergens</span>}
                  </span>
                  <span aria-hidden className="h-1 w-1 rounded-full bg-[#F08DB5]" />
                  <VegMark diet={dish.diet} testId="dish-detail-diet" />
                  <span aria-hidden className="h-1 w-1 rounded-full bg-[#F08DB5]" />
                  <ServePrices dish={dish} size="detail" testId="dish-detail-price" />
                </div>
                <div className="mt-5 flex justify-center lg:justify-start landscape:md:justify-start"><AllergenKey testId="dish-allergen-key" /></div>
                {dish.course !== "beverage" && dish.has_science && <div className="mx-auto mt-6 max-w-2xl lg:mx-0"><ScienceReveal kind="dish" targetId={dish.id} align="center" testId="dish-science" /></div>}
                <p className="font-ui mt-8 text-[15px] text-[#0B2E44]">Please inform your server of any allergies. Govt. taxes as applicable.</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
